import { useState, useEffect } from 'react';
import { pdfjs } from 'react-pdf';
import type { PDFDocumentProxy } from 'pdfjs-dist';

// Configure PDF.js worker using CDN
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface UsePdfLoaderReturn {
  loading: boolean;
  error: Error | null;
  totalPages: number;
}

/**
 * Custom hook to handle loading multiple single-page PDFs
 * @param pdfUrls Array of PDF URLs to load
 * @returns Loading state, error state, and total number of pages
 */
export const usePdfLoader = (pdfUrls: string[]): UsePdfLoaderReturn => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    if (!pdfUrls || pdfUrls.length === 0) {
      setLoading(false);
      setTotalPages(0);
      return;
    }

    const loadPdfs = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load all PDFs in parallel
        const loadPromises = pdfUrls.map(async (url) => {
          try {
            const loadingTask = pdfjs.getDocument(url);
            const pdf = await loadingTask.promise;
            return pdf;
          } catch (err) {
            console.error(`Error loading PDF: ${url}`, err);
            return null; // Skip failed PDFs
          }
        });

        const results = await Promise.all(loadPromises);

        // Filter out failed PDFs and count total pages
        const successfulPdfs = results.filter((pdf): pdf is PDFDocumentProxy => pdf !== null);
        const pageCount = successfulPdfs.reduce((total, pdf) => total + pdf.numPages, 0);

        setTotalPages(pageCount);

        if (successfulPdfs.length === 0) {
          throw new Error('No PDFs could be loaded');
        }
      } catch (err) {
        console.error('Error loading PDFs:', err);
        setError(err instanceof Error ? err : new Error('Failed to load PDFs'));
      } finally {
        setLoading(false);
      }
    };

    loadPdfs();
  }, [pdfUrls]);

  return { loading, error, totalPages };
};
